import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, formatDateCompact } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  FolderKanban, Plus, Loader2, Calendar, Users, Trash2,
  Pencil, ChevronRight, Clock, DollarSign, Hash
} from 'lucide-react';
import { toast } from 'sonner';

const projectStatusConfig = {
  planificacion: { label: 'Planificacion', color: 'bg-blue-100 text-blue-700' },
  en_progreso: { label: 'En Progreso', color: 'bg-amber-100 text-amber-700' },
  en_pausa: { label: 'En Pausa', color: 'bg-gray-100 text-gray-600' },
  completado: { label: 'Completado', color: 'bg-emerald-100 text-emerald-700' },
  cancelado: { label: 'Cancelado', color: 'bg-rose-100 text-rose-700' },
};

const COLOR_PALETTE = [
  ['#E84057', '#EB5B3C', '#E8782A', '#E8A52A', '#D5C52A', '#56C556', '#36B09F', '#3A8DD4', '#5E6BC4', '#9B59B6'],
  ['#F06292', '#FF8A65', '#FFB74D', '#FFD54F', '#E6EE9C', '#81C784', '#4DB6AC', '#64B5F6', '#7986CB', '#BA68C8'],
  ['#F48FB1', '#FFAB91', '#FFCC80', '#FFE082', '#F0F4C3', '#A5D6A7', '#80CBC4', '#90CAF9', '#9FA8DA', '#CE93D8'],
  ['#F8BBD0', '#FFCCBC', '#FFE0B2', '#FFF9C4', '#F9FBE7', '#C8E6C9', '#B2DFDB', '#BBDEFB', '#C5CAE9', '#E1BEE7'],
  ['#880E4F', '#BF360C', '#E65100', '#F57F17', '#827717', '#1B5E20', '#004D40', '#01579B', '#1A237E', '#4A148C'],
  ['#37474F', '#455A64', '#546E7A', '#78909C', '#90A4AE', '#B0BEC5', '#CFD8DC', '#ECEFF1', '#FFFFFF', '#263238'],
];

export default function ProjectsPage() {
  const { user, isJefatura } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);

  const [form, setForm] = useState({
    name: '', code: '', description: '', color: '#3B82F6',
    start_date: '', end_date: '', status: 'planificacion',
    budget: '', member_ids: [], leader_ids: []
  });

  useEffect(() => {
    fetchProjects();
    if (isJefatura) fetchUsers();
  }, [isJefatura]);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API_URL}/projects`);
      setProjects(res.data);
    } catch (err) {
      toast.error('Error al cargar proyectos');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/users`);
      setUsers(res.data.filter(u => u.role === 'solucionador' || u.role === 'jefatura'));
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const openCreateDialog = () => {
    setEditingProject(null);
    setForm({ name: '', code: '', description: '', color: '#3B82F6', start_date: '', end_date: '', status: 'planificacion', budget: '', member_ids: [], leader_ids: [] });
    setDialogOpen(true);
  };

  const openEditDialog = (proj) => {
    setEditingProject(proj);
    setForm({
      name: proj.name,
      code: proj.code || '',
      description: proj.description || '',
      color: proj.color,
      start_date: proj.start_date ? proj.start_date.split('T')[0] : '',
      end_date: proj.end_date ? proj.end_date.split('T')[0] : '',
      status: proj.status,
      budget: proj.budget || '',
      member_ids: [],
      leader_ids: []
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        code: form.code || null,
        description: form.description || null,
        color: form.color,
        start_date: form.start_date ? `${form.start_date}T00:00:00Z` : null,
        end_date: form.end_date ? `${form.end_date}T00:00:00Z` : null,
        budget: form.budget ? parseFloat(form.budget) : 0,
      };
      if (editingProject) {
        payload.status = form.status;
        await axios.patch(`${API_URL}/projects/${editingProject.id}`, payload);
        toast.success('Proyecto actualizado');
      } else {
        payload.member_ids = form.member_ids;
        payload.leader_ids = form.leader_ids;
        await axios.post(`${API_URL}/projects`, payload);
        toast.success('Proyecto creado');
      }
      setDialogOpen(false);
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar proyecto');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Estas seguro de eliminar este proyecto? Se eliminaran todas las tareas asociadas.')) return;
    try {
      await axios.delete(`${API_URL}/projects/${id}`);
      toast.success('Proyecto eliminado');
      fetchProjects();
    } catch (err) {
      toast.error('Error al eliminar proyecto');
    }
  };

  const toggleMember = (userId) => {
    setForm(prev => ({
      ...prev,
      member_ids: prev.member_ids.includes(userId)
        ? prev.member_ids.filter(id => id !== userId)
        : [...prev.member_ids, userId],
      // Remove from leaders if removing from members
      leader_ids: !prev.member_ids.includes(userId)
        ? prev.leader_ids
        : prev.leader_ids.filter(id => id !== userId)
    }));
  };

  const toggleLeader = (userId) => {
    setForm(prev => ({
      ...prev,
      leader_ids: prev.leader_ids.includes(userId)
        ? prev.leader_ids.filter(id => id !== userId)
        : [...prev.leader_ids, userId],
      // Auto-add to members
      member_ids: prev.member_ids.includes(userId)
        ? prev.member_ids
        : [...prev.member_ids, userId]
    }));
  };

  const getDaysCount = (start, end) => {
    if (!start || !end) return null;
    const s = new Date(start);
    const e = new Date(end);
    return Math.max(0, Math.ceil((e - s) / (1000 * 60 * 60 * 24)));
  };

  const formatBudget = (val) => {
    if (!val || val === 0) return null;
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(val);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#7FD02E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="projects-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-gray-800">Proyectos</h1>
          <p className="text-gray-500">Planificacion operacional y gestion de proyectos</p>
        </div>
        {isJefatura && (
          <Button
            className="bg-[#7FD02E] hover:bg-[#6BC024] text-white shadow-sm"
            onClick={openCreateDialog}
            data-testid="new-project-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Proyecto
          </Button>
        )}
      </div>

      {projects.length === 0 ? (
        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <FolderKanban className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-lg font-medium text-gray-700">No hay proyectos</p>
            <p className="text-gray-500 text-sm mt-1">Crea un proyecto para comenzar a planificar</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {projects.map(proj => (
            <Card
              key={proj.id}
              className="border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
              data-testid={`project-card-${proj.id}`}
              onClick={() => navigate(`/projects/${proj.id}`)}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-3 h-3 rounded-full shrink-0"
                      style={{ backgroundColor: proj.color }}
                    />
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-800 truncate">{proj.name}</h3>
                      {proj.code && (
                        <span className="text-xs font-mono text-gray-400">{proj.code}</span>
                      )}
                    </div>
                  </div>
                  <Badge className={projectStatusConfig[proj.status]?.color + ' text-xs shrink-0'}>
                    {projectStatusConfig[proj.status]?.label}
                  </Badge>
                </div>

                {proj.description && (
                  <p className="text-sm text-gray-500 line-clamp-2 mb-3">{proj.description}</p>
                )}

                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Progreso</span>
                    <span className="font-medium text-gray-700">{proj.progress}%</span>
                  </div>
                  <Progress value={proj.progress} className="h-2" />

                  {(proj.budget > 0 || getDaysCount(proj.start_date, proj.end_date) !== null) && (
                    <div className="flex items-center gap-3 text-xs text-gray-500 pt-1">
                      {proj.budget > 0 && (
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3.5 h-3.5" />
                          {formatBudget(proj.budget)}
                        </span>
                      )}
                      {getDaysCount(proj.start_date, proj.end_date) !== null && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {getDaysCount(proj.start_date, proj.end_date)} dias
                        </span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between text-xs text-gray-400 pt-1">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        {proj.member_count}
                      </span>
                      <span className="flex items-center gap-1">
                        <FolderKanban className="w-3.5 h-3.5" />
                        {proj.task_count} tareas
                      </span>
                    </div>
                    {proj.end_date && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDateCompact(proj.end_date)}
                      </span>
                    )}
                  </div>
                </div>

                {isJefatura && (
                  <div className="flex items-center gap-1 mt-3 pt-3 border-t border-gray-100 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost" size="sm"
                      className="text-gray-400 hover:text-[#7FD02E] h-8"
                      onClick={(e) => { e.stopPropagation(); openEditDialog(proj); }}
                      data-testid={`edit-project-${proj.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      className="text-gray-400 hover:text-destructive h-8"
                      onClick={(e) => { e.stopPropagation(); handleDelete(proj.id); }}
                      data-testid={`delete-project-${proj.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                    </Button>
                    <div className="ml-auto">
                      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#7FD02E] transition-colors" />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle className="text-gray-800">
                {editingProject ? 'Editar Proyecto' : 'Nuevo Proyecto'}
              </DialogTitle>
              <DialogDescription className="text-gray-500">
                {editingProject ? 'Modifica los datos del proyecto' : 'Crea un nuevo proyecto para tu equipo'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label className="text-gray-700">Nombre *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Nombre del proyecto"
                    required
                    className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                    data-testid="project-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Codigo</Label>
                  <Input
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="PROJ-001"
                    className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E] font-mono text-sm"
                    data-testid="project-code-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Descripcion</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Descripcion del proyecto..."
                  rows={3}
                  className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                  data-testid="project-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Presupuesto (CLP)</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.budget}
                  onChange={(e) => setForm({ ...form, budget: e.target.value })}
                  placeholder="0"
                  className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                  data-testid="project-budget-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Color</Label>
                <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(10, 1fr)' }}>
                  {COLOR_PALETTE.flat().map(c => (
                    <button
                      key={c}
                      type="button"
                      className={`w-full aspect-square rounded-sm border transition-all ${form.color === c ? 'border-gray-800 ring-1 ring-gray-800 scale-110 z-10' : 'border-transparent hover:border-gray-300'}`}
                      style={{ backgroundColor: c }}
                      onClick={() => setForm({ ...form, color: c })}
                    />
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Fecha inicio</Label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                    data-testid="project-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Fecha fin</Label>
                  <Input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    className="border-gray-300 focus:border-[#7FD02E] focus:ring-[#7FD02E]"
                    data-testid="project-end-date"
                  />
                </div>
              </div>
              {editingProject && (
                <div className="space-y-2">
                  <Label className="text-gray-700">Estado</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="border-gray-300" data-testid="project-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="planificacion">Planificacion</SelectItem>
                      <SelectItem value="en_progreso">En Progreso</SelectItem>
                      <SelectItem value="en_pausa">En Pausa</SelectItem>
                      <SelectItem value="completado">Completado</SelectItem>
                      <SelectItem value="cancelado">Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {!editingProject && users.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-gray-700">Equipo del proyecto</Label>
                  <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto p-2 space-y-1">
                    {users.map(u => (
                      <div
                        key={u.id}
                        className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                          form.member_ids.includes(u.id) ? 'bg-[#7FD02E]/10' : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={form.member_ids.includes(u.id)}
                          onChange={() => toggleMember(u.id)}
                          className="rounded border-gray-300 text-[#7FD02E] focus:ring-[#7FD02E]"
                        />
                        <span className="text-sm text-gray-700 flex-1">{u.first_name} {u.last_name}</span>
                        {form.member_ids.includes(u.id) && (
                          <button
                            type="button"
                            onClick={() => toggleLeader(u.id)}
                            className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                              form.leader_ids.includes(u.id)
                                ? 'bg-amber-100 text-amber-700 border-amber-200'
                                : 'bg-gray-50 text-gray-400 border-gray-200 hover:bg-amber-50 hover:text-amber-600'
                            }`}
                            data-testid={`toggle-leader-${u.id}`}
                          >
                            {form.leader_ids.includes(u.id) ? 'Jefe Proyecto' : 'Hacer Jefe'}
                          </button>
                        )}
                        <Badge className="text-xs bg-gray-100 text-gray-500">{u.role}</Badge>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400">Selecciona miembros y marca quienes seran Jefes de Proyecto</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" className="text-gray-600" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving || !form.name.trim()}
                className="bg-[#7FD02E] hover:bg-[#6BC024] text-white"
                data-testid="submit-project-btn"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingProject ? 'Guardar' : 'Crear Proyecto'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
